// Baselayer Results Explorer — two-column "Your Search" vs "Matched" compare
// block, used by the Results + Watchlists tabs. Each row pairs a label with the
// searched value, the matched value, and an optional trailing badge.

import * as React from "react";

export interface CompareRow {
  label: string;
  searched: React.ReactNode;
  matched: React.ReactNode;
  /** Optional trailing element (e.g. a MatchBadge). */
  badge?: React.ReactNode;
}

interface SideBySideCompareProps {
  rows: CompareRow[];
  searchedHeading?: string;
  matchedHeading?: string;
}

export function SideBySideCompare({
  rows,
  searchedHeading = "Your Search",
  matchedHeading = "Matched",
}: SideBySideCompareProps) {
  return (
    <div className="rounded-lg border border-border-light bg-white-pure shadow-sm overflow-hidden">
      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-px bg-onyx">
        <ColumnHeading>{searchedHeading}</ColumnHeading>
        <ColumnHeading>{matchedHeading}</ColumnHeading>
      </div>
      <dl className="m-0 divide-y divide-border-light">
        {rows.map((row) => (
          <div
            key={row.label}
            className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-px"
          >
            <Cell label={row.label} value={row.searched} />
            <Cell label={row.label} value={row.matched} badge={row.badge} />
          </div>
        ))}
      </dl>
    </div>
  );
}

function ColumnHeading({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-onyx px-space-3 py-space-2 font-display text-[10px] font-semibold uppercase tracking-label text-gold">
      {children}
    </div>
  );
}

function Cell({
  label,
  value,
  badge,
}: {
  label: string;
  value: React.ReactNode;
  badge?: React.ReactNode;
}) {
  return (
    <div className="bg-white-pure px-space-3 py-space-3">
      <dt className="font-display text-label font-semibold uppercase tracking-label text-gray-mid">
        {label}
      </dt>
      <dd className="m-0 mt-[2px] flex items-center gap-space-2 text-small text-onyx break-words">
        <span>{value}</span>
        {badge}
      </dd>
    </div>
  );
}
